import { __assign, __rest } from "tslib";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useRef, useLayoutEffect } from "react";
import clsx from "clsx";
import styles from "./styles.css.js";
import { getBaseProps } from "../../base-component";
var SelectableItem = function(_a, ref) {
    var _b;
    var content = _a.children, ariaSelected = _a.ariaSelected, ariaChecked = _a.ariaChecked, selected = _a.selected, highlighted = _a.highlighted, disabled = _a.disabled, hasBackground = _a.hasBackground, isParent = _a.isParent, isChild = _a.isChild, virtualPosition = _a.virtualPosition, padBottom = _a.padBottom, isNextSelected = _a.isNextSelected, useInteractiveGroups = _a.useInteractiveGroups, screenReaderContent = _a.screenReaderContent, ariaPosinset = _a.ariaPosinset, ariaSetsize = _a.ariaSetsize, highlightType = _a.highlightType, restProps = __rest(_a, [
        "children",
        "ariaSelected",
        "ariaChecked",
        "selected",
        "highlighted",
        "disabled",
        "hasBackground",
        "isParent",
        "isChild",
        "virtualPosition",
        "padBottom",
        "isNextSelected",
        "useInteractiveGroups",
        "screenReaderContent",
        "ariaPosinset",
        "ariaSetsize",
        "highlightType"
    ]);
    var _c = getBaseProps(restProps), className = _c.className, rest = __rest(_c, [
        "className"
    ]);
    var classNames = clsx(className, styles["selectable-item"], (_b = {}, _b[styles.selected] = selected, _b[styles.highlighted] = highlighted, _b[styles["has-background"]] = hasBackground, _b[styles.parent] = isParent, _b[styles.child] = isChild, _b[styles["is-keyboard"]] = highlightType === "keyboard", _b[styles.disabled] = disabled, _b[styles.virtual] = virtualPosition !== undefined, _b[styles["pad-bottom"]] = padBottom, _b[styles["next-item-selected"]] = isNextSelected, _b[styles.interactiveGroups] = useInteractiveGroups, _b));
    var contentRef = useRef(null);
    var screenReaderContentRef = useRef(null);
    useLayoutEffect(function() {
        // the state of aria-hidden and announcement is not set back because NVDA+Firefox would announce
        // the item which lost highlight
        // set aria-hidden true when there is announcement content, so that screen reader still announce
        // meaningful content when navigate with screen reader cursor
        // imperatively update to avoid announcement made multiple times when content updates
        if (highlighted && screenReaderContent) {
            if (contentRef.current) {
                contentRef.current.setAttribute("aria-hidden", "true");
            }
            if (screenReaderContentRef.current) {
                screenReaderContentRef.current.textContent = screenReaderContent;
            }
        }
    }, [
        highlighted,
        screenReaderContent,
        contentRef,
        screenReaderContentRef
    ]);
    var style = virtualPosition !== undefined ? {
        transform: "translateY(".concat(virtualPosition, "px)")
    } : undefined;
    var a11yProperties = {
        "aria-disabled": disabled
    };
    if (isParent && !useInteractiveGroups) {
        a11yProperties["aria-hidden"] = true;
    }
    if (ariaSelected !== undefined) {
        a11yProperties["aria-selected"] = ariaSelected;
    }
    // Safari+VO needs aria-checked for multi-selection. Otherwise it only announces selected option even though another option is highlighted.
    if (ariaChecked !== undefined) {
        a11yProperties["aria-checked"] = ariaChecked;
    }
    if (ariaPosinset && ariaSetsize) {
        a11yProperties["aria-posinset"] = ariaPosinset;
        a11yProperties["aria-setsize"] = ariaSetsize;
    }
    return /*#__PURE__*/ React.createElement("li", __assign({
        role: "option",
        className: classNames,
        style: style
    }, a11yProperties, rest), /*#__PURE__*/ React.createElement("div", {
        className: styles["option-content"],
        ref: contentRef
    }, content), /*#__PURE__*/ React.createElement("div", {
        className: styles["measure-strut"],
        ref: ref
    }), /*#__PURE__*/ React.createElement("div", {
        className: styles["screenreader-content"],
        ref: screenReaderContentRef
    }));
};
export default /*#__PURE__*/ React.forwardRef(SelectableItem); //# sourceMappingURL=index.js.map
